import { PhoenixTrpcClient } from 'apps/phoenix/src/app/utils';
import { debounceTime, from } from 'rxjs';

import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'phoenix-os-query-field',
  template: `
    <mat-form-field class="w-full">
      <mat-label>{{ title }}</mat-label>
      <textarea
        matTooltipPosition="below"
        [matTooltip]="tooltip"
        matInput
        [rows]="inputRows"
        matTextareaAutosize
        matAutosizeMinRows="1"
        matAutosizeMaxRows="5"
        [name]="title.replace(' ', '').toLocaleLowerCase()"
        [attr.aria-label]="'OSQueryFieldInput'"
        [maxlength]="MAX_QUERY_LENGTH"
        [formControl]="control"
        (ngModelChange)="onSearch($event)"
        [attr.title]="''"
      ></textarea>
      @if (queryInProgress) {
        <mat-spinner
          [attr.aria-label]="'OSQueryFieldLoading'"
          class="mx-[12px]"
          matSuffix
          [diameter]="24"
        ></mat-spinner>
      } @else {
        <mat-icon
          [attr.aria-label]="'OSQueryFieldStatus'"
          [ngClass]="!control.errors ? 'text-green-600' : 'text-red-500'"
          matSuffix
        >
          {{ control.errors ? 'error' : 'check_circle' }}
        </mat-icon>
      }
    </mat-form-field>
  `,
  styles: [],
  standalone: false,
})
export class OSQueryFieldComponent implements OnInit, OnChanges {
  @Input() title = '';
  @Input() control: FormControl = new FormControl('');
  @Input() inputRows = 1;
  @Input() tooltip = 'Supports both simple and structured JSON queries';
  @Input() validateQuery = true; // Skip validation for literal string inputs when false (e.g., exact text match).

  @Output() queryChange = new EventEmitter();
  @Output() submitChange = new EventEmitter();

  loadingError = { queryInProgress: 'Query is in progress!' };
  queryError = { queryError: 'Query has an error!' };

  MAX_QUERY_LENGTH = 1000;
  queryInProgress = false;
  activeRequests = 0;

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      this.submitChange.emit();
    }
  }

  readonly validQuery = signal(true);
  readonly phoenixTrpcClient = inject(PhoenixTrpcClient);

  constructor() {
    effect(() => {
      this.queryChange.emit(this.validQuery());
    });
  }

  ngOnInit(): void {
    this.control.valueChanges.pipe(debounceTime(200)).subscribe(value => {
      this.onBlur();
      this.setErrors();
      if (value === '') {
        this.control.setErrors(null);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['validateQuery'];
    if (change && !change.firstChange) {
      // Re-validate if enabled; otherwise clear error state.
      if (this.validateQuery) {
        this.onBlur();
      } else {
        this.setValidState();
      }
    }
  }

  private setValidState(): void {
    this.activeRequests = 0;
    this.queryInProgress = false;
    this.validQuery.set(true);
    this.setErrors();
  }

  onSearch(response: string) {
    // No validation runs when validateQuery is off, so never show the spinner.
    this.queryInProgress = this.validateQuery && !!response;
    this.setErrors();
  }

  onBlur() {
    if (!this.validateQuery) {
      this.setValidState();
      return;
    }
    if (this.control.value && this.control.value !== '') {
      this.activeRequests++;
      this.queryInProgress = true;
      this.setErrors();

      this.onQueryValidation(this.control.value);
    }
  }

  setErrors() {
    const controlErrors = this.control?.errors ?? {};
    delete controlErrors?.['queryError'];
    delete controlErrors?.['queryInProgress'];

    const errors = {
      ...controlErrors,
      ...(!this.validQuery() && { ...this.queryError }),
      ...(this.queryInProgress && { ...this.loadingError }),
    };
    this.control.setErrors(Object.keys(errors).length > 0 ? errors : null);
  }

  async onQueryValidation(value: string) {
    if (value && value !== '') {
      from(
        this.phoenixTrpcClient.queryValidator.validate.query(value)
      ).subscribe({
        next: isValid => {
          // Ignore a response that arrives after validation was disabled (e.g. exact text match toggled on mid-request).
          if (!this.validateQuery) return;
          this.validQuery.set(isValid);
          this.handleQueryResponse();
        },
        error: () => {
          if (!this.validateQuery) return;
          this.validQuery.set(false);
          this.handleQueryResponse();
        },
      });
    }
  }

  handleQueryResponse() {
    // Clamp to avoid going negative if a request is settled after the counter
    // was reset (e.g. validation disabled while a request was in flight).
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    this.queryInProgress = this.activeRequests !== 0;
    this.setErrors();
  }
}
